/**
 * Account create/edit wizard — Roomgrid rail layout (the locks-management
 * account-wizard pattern): left rail with numbered steps + live summary,
 * content column on the right, footer with Cancel / Back / Next / Save.
 *
 * Steps: Account info → Users → Cost center → Applications → Groups →
 * Templatify integration. One react-hook-form instance spans all steps;
 * Next validates only the mounted step.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  Save as SaveIcon,
} from "@mui/icons-material";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { createAccount, updateAccount } from "../services/accountService";
import { buildAccountPayload } from "../transforms/buildAccountPayload";
import { getAccountLookup } from "../hooks/useAccountLookup";
import type { AccountFormValues } from "../types/account";
import AccountInfoStep from "./steps/AccountInfoStep";
import TemplatifyStep from "./steps/TemplatifyStep";
import GroupsStep from "./steps/GroupsStep";
import WizardRail, { type WizardRailStep } from "./WizardRail";

interface AccountFormWizardProps {
  mode: "create" | "edit";
  initialValues: AccountFormValues;
  recordId?: string;
}

const STEP_KEYS = [
  "accountInfo",
  "accountAccess",
  "templatify",
  "groups",
  "users",
  "costCenter",
] as const;

export const AccountFormWizard: React.FC<AccountFormWizardProps> = ({
  mode,
  initialValues,
  recordId,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const hasReset = useRef(false);

  const methods = useForm<AccountFormValues>({
    defaultValues: initialValues,
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const accountName = methods.watch("accountName");
  const useTemplatify = methods.watch("useTemplatify");
  const useCostCenter = methods.watch("useCostCenter");
  const communicationTypes = methods.watch("communicationTypes");
  const validityTimeFrame = methods.watch("validityTimeFrame");
  const applicationsWatched = methods.watch("applications");
  const templatifyExtraApps = methods.watch("templatifyExtraApplicationIds");
  // At least one application linked, whichever way: the Account Information
  // multi-select (notify rows) or the Templatify Integration picker.
  const applicationsValid =
    (Array.isArray(applicationsWatched) &&
      applicationsWatched.some((r) => Boolean(r.applicationId))) ||
    (Boolean(useTemplatify) && (templatifyExtraApps ?? []).length > 0);

  const isValidityPast = useMemo(() => {
    if (!validityTimeFrame) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const picked = new Date(`${validityTimeFrame}T00:00:00`);
    picked.setHours(0, 0, 0, 0);
    return picked < today;
  }, [validityTimeFrame]);

  const t = (key: string, fallback: string): string =>
    translate(`resources.accounts.wizard.${key}`, { _: fallback });

  const steps: WizardRailStep[] = useMemo(
    () => [
      {
        key: "accountInfo",
        title: translate("resources.accounts.tabs.accountInfo"),
        desc: t("accountInfoDesc", "Name, providers, license & apps"),
      },
      {
        key: "accountAccess",
        title: t("accountAccess", "Account access"),
        desc: t("accountAccessDesc", "Rate limits & IP whitelist"),
      },
      {
        key: "templatify",
        title: translate("resources.accounts.tabs.templatifyIntegration"),
        desc: t("templatifyDesc", "Template rendering account"),
      },
      {
        key: "groups",
        title: t("groups", "Groups"),
        desc: t("groupsDesc", "Notification groups per app"),
      },
      {
        key: "users",
        title: t("users", "Users"),
        desc: t("usersDesc", "Optional account users"),
      },
      {
        key: "costCenter",
        title: t("costCenter", "Cost center"),
        desc: t("costCenterDesc", "Credit billing assignment"),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate],
  );

  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;

  const appsCount = (applicationsWatched ?? []).filter((r) =>
    Boolean(r.applicationId),
  ).length;
  const summaryMeta = [
    `${appsCount} APPS`,
    `${(communicationTypes ?? []).length} CHANNELS`,
    useCostCenter ? "COST CENTER" : null,
    useTemplatify ? "TEMPLATIFY" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Free navigation: steps never block. Validation runs once, on Save —
  // the submit handler jumps to the step owning the first failing field.
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));
  const goNext = () => setCurrentStep((s) => Math.min(totalSteps - 1, s + 1));

  const handleDiscard = () => redirect("list", "apps/accounts");

  /** Map a react-hook-form field path to the wizard step that renders it. */
  const stepForField = (fieldPath: string): number => {
    const top = fieldPath.split(".")[0];
    switch (top) {
      case "rateLimitApplies":
      case "rateLimitPerMin":
      case "rateLimitPerDay":
      case "whitelistApplies":
      case "whiteListedIps":
        return 1;
      case "accountMode":
      case "rmkTemplateAccountName":
      case "rmkTemplateApiKey":
      case "rmkExistTemplateAccount":
      case "rmkExistTemplateApiKey":
        return 2;
      case "applicationGroups":
        return 3;
      case "users":
      case "addUser":
        return 4;
      case "costCenterId":
      case "useCostCenter":
        return 5;
      default:
        return 0;
    }
  };

  const onInvalid = (errors: Record<string, unknown>): void => {
    const first = Object.keys(errors)[0];
    if (first) setCurrentStep(stepForField(first));
    notify(
      translate("resources.accounts.wizard.fixErrors", {
        _: "Some required fields are missing — check the highlighted step",
      }),
      { type: "warning" },
    );
  };

  const onSubmit = async (values: AccountFormValues): Promise<void> => {
    // Cross-step guards. RHF only validates MOUNTED fields, and with free
    // navigation a step may never have been visited — so the core required
    // fields are re-checked here explicitly, jumping to their step.
    const notifyMissing = (step: number, field?: string): void => {
      setCurrentStep(step);
      notify(
        field
          ? translate("resources.accounts.wizard.fixField", {
              field,
              _: `Required field missing: ${field}`,
            })
          : translate("resources.accounts.wizard.fixErrors", {
              _: "Some required fields are missing — check the highlighted step",
            }),
        { type: "warning" },
      );
    };
    // API key is required only when CREATING — on edit it's left blank to keep
    // the existing key (matches the field's own required={mode === 'create'}).
    const missingStep0Field = !values.accountName?.trim()
      ? translate("resources.accounts.fields.accountName", { _: "Account name" })
      : mode === "create" && !values.apiKey?.trim()
        ? translate("resources.accounts.fields.apiKey", { _: "API key" })
        : !values.licenseId
          ? translate("resources.accounts.fields.license", { _: "License" })
          : // The backend's ValidityTimeFrame is a non-nullable DateTime.
            !values.validityTimeFrame
            ? translate("resources.accounts.fields.validityTimeFrame", {
                _: "License validity time frame",
              })
            : null;
    if (missingStep0Field) {
      notifyMissing(0, missingStep0Field);
      return;
    }
    if (values.useTemplatify) {
      if (
        values.accountMode === "new" &&
        (!values.rmkTemplateAccountName?.trim() ||
          !values.rmkTemplateApiKey?.trim())
      ) {
        notifyMissing(2);
        return;
      }
      if (
        values.accountMode === "existing" &&
        !values.rmkExistTemplateAccount?.id
      ) {
        notifyMissing(2);
        return;
      }
    }
    if (isValidityPast) {
      setCurrentStep(0);
      notify(
        translate("resources.accounts.validation.pastDateNotAllowed"),
        { type: "warning" },
      );
      return;
    }
    if (!applicationsValid) {
      setCurrentStep(0);
      notify(
        translate("resources.accounts.validation.applicationRequired"),
        { type: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const [notifyApps, templatifyApps] = await Promise.all([
        getAccountLookup("notifyApps"),
        getAccountLookup("templatifyApps"),
      ]);
      // The old Use-Cost-Center / Add-Users checkboxes are gone from the
      // UI — the flags the payload builder expects are derived from the
      // actual data instead.
      const derived: AccountFormValues = {
        ...values,
        useCostCenter: Boolean(values.costCenterId),
        addUser: (values.users ?? []).length > 0,
      };
      const body = buildAccountPayload(derived, { notifyApps, templatifyApps });
      if (mode === "edit" && recordId) {
        await updateAccount(recordId, body);
        notify("resources.accounts.notifications.updated", { type: "success" });
      } else {
        await createAccount(body);
        notify("resources.accounts.notifications.created", { type: "success" });
      }
      refresh();
      redirect("list", "apps/accounts");
    } catch (error: unknown) {
      notify(
        extractErrorMessage(
          error,
          "resources.accounts.notifications.saveFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (hasReset.current) return;
    methods.reset(initialValues);
    hasReset.current = true;
  }, [initialValues]);

  const stepKey = STEP_KEYS[currentStep];

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
        noValidate
        sx={{
          width: "100%",
          maxWidth: 1100,
          height: "calc(100vh - 96px)",
          minHeight: 480,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "baseline",
              gap: "10px",
              p: "16px 20px",
              borderBottom: `1px solid ${rg.border}`,
            }}
          >
            <Typography
              sx={{ fontSize: "15px", fontWeight: 700, letterSpacing: "-0.02em" }}
            >
              {accountName ||
                translate("resources.accounts.account.title", {
                  _: "Account",
                })}
            </Typography>
            <Typography
              className="rg-mono"
              sx={{ fontSize: "10.5px", color: rg.text3 }}
            >
              {t("stepOf", "Step")} {currentStep + 1} / {totalSteps}
            </Typography>
          </Box>

          {/* Rail + content */}
          <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
            {!isMobile && (
              <WizardRail
                steps={steps}
                current={currentStep}
                onStepClick={(idx) => setCurrentStep(idx)}
                summaryTitle={
                  accountName || t("untitled", "Untitled account")
                }
                summaryMeta={summaryMeta}
              />
            )}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflowY: "auto",
                p: { xs: "16px 14px", md: "20px 22px" },
              }}
            >
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "10.5px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontWeight: 600,
                    color: rg.accent,
                    mb: "12px",
                  }}
                >
                  {steps[currentStep].title}
                </Typography>
              )}

              {stepKey === "accountInfo" && (
                <AccountInfoStep mode={mode} sections={["general", "ai"]} />
              )}
              {stepKey === "accountAccess" && (
                <AccountInfoStep mode={mode} sections={["limits"]} />
              )}
              {stepKey === "users" && (
                <AccountInfoStep mode={mode} sections={["users"]} />
              )}
              {stepKey === "costCenter" && (
                <AccountInfoStep mode={mode} sections={["costCenter"]} />
              )}
              {stepKey === "groups" && <GroupsStep />}
              {stepKey === "templatify" && (
                <Box>
                  <AccountInfoStep
                    mode={mode}
                    sections={["templatifyToggle"]}
                  />
                  {useTemplatify && (
                    <Box sx={{ mt: 2 }}>
                      <TemplatifyStep />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: "14px 20px",
              backgroundColor: rg.bg2,
              borderTop: `1px solid ${rg.border}`,
            }}
          >
            <Button variant="text" onClick={handleDiscard}>
              {translate("ra.action.cancel", { _: "Cancel" })}
            </Button>
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              {currentStep > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack sx={{ fontSize: 15 }} />}
                  onClick={goBack}
                >
                  {translate("resources.accounts.actions.back")}
                </Button>
              )}
              {!isLastStep && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForward sx={{ fontSize: 15 }} />}
                  onClick={goNext}
                >
                  {translate("resources.accounts.actions.next")}
                </Button>
              )}
              {isLastStep && (
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    submitting ? (
                      <CircularProgress size={14} sx={{ color: "inherit" }} />
                    ) : (
                      <SaveIcon sx={{ fontSize: 15 }} />
                    )
                  }
                  disabled={submitting}
                >
                  {translate("resources.accounts.actions.submit")}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </FormProvider>
  );
};

export default AccountFormWizard;
